import { getApiUrl } from '../utils/api';
import { getAuthToken } from '../utils/sessionAuth';

/**
 * AI Service for generating content using various AI APIs
 */

/**
 * Generate dashboard description using OpenRouter DeepSeek API
 * @param {Object} params - Description generation parameters
 * @param {string} params.title - Dashboard title
 * @param {string} [params.category] - Dashboard category
 * @param {string[]} [params.toolsUsed] - Array of tools used
 * @param {string} [params.existingDescription] - Current description to improve
 * @returns {Promise<Object>} Generated description response
 */
export const generateDescription = async ({
  title,
  category,
  toolsUsed = [],
  existingDescription
}) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const formData = new FormData();
    formData.append('title', title);
    
    if (category) {
      formData.append('category', category);
    }
    
    if (toolsUsed && toolsUsed.length > 0) {
      toolsUsed.forEach(tool => {
        formData.append('tools_used', tool);
      });
    }
    
    if (existingDescription) {
      formData.append('existing_description', existingDescription);
    }

    const response = await fetch(getApiUrl('dashboards/generate-description'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to generate description');
    }

    return {
      success: true,
      description: data.description.description,
      shortDescription: data.description.shortDescription,
      source: data.source // 'ai' or 'fallback'
    };

  } catch (error) {
    console.error('Error generating description:', error);
    throw error;
  }
};

/**
 * Generate SEO metadata using Google Gemini API
 * @param {Object} params - Metadata generation parameters
 * @param {string} params.title - Dashboard title
 * @param {string} [params.category] - Dashboard category
 * @param {string} [params.about] - Dashboard description
 * @param {string[]} [params.toolsUsed] - Array of tools used
 * @returns {Promise<Object>} Generated metadata response
 */
export const generateMetadata = async ({
  title,
  category,
  about,
  toolsUsed = []
}) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const formData = new FormData();
    formData.append('title', title);
    
    if (category) {
      formData.append('category', category);
    }
    
    if (about) {
      formData.append('about', about);
    }
    
    if (toolsUsed && toolsUsed.length > 0) {
      toolsUsed.forEach(tool => {
        formData.append('tools_used', tool);
      });
    }

    const response = await fetch(getApiUrl('dashboards/generate-metadata'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to generate metadata');
    }

    return {
      success: true,
      metadata: data.metadata,
      source: data.source // 'ai' or 'fallback'
    };

  } catch (error) {
    console.error('Error generating metadata:', error);
    throw error;
  }
};

/**
 * Check if AI services are available
 * @returns {Object} Availability status of AI services
 */
export const checkAIServiceAvailability = () => {
  return {
    descriptionGeneration: true, // OpenRouter DeepSeek (free tier)
    metadataGeneration: true,    // Google Gemini
    imageAnalysis: false         // Not implemented yet
  };
};

/**
 * Get AI service information
 * @returns {Object} Information about available AI services
 */
export const getAIServiceInfo = () => {
  return {
    descriptionGeneration: {
      provider: 'OpenRouter DeepSeek V3',
      model: 'deepseek-chat-v3-0324:free',
      cost: 'Free',
      description: 'Generate compelling dashboard descriptions'
    },
    metadataGeneration: {
      provider: 'Google Gemini',
      model: 'gemini-2.0-flash-exp',
      cost: 'Free tier available',
      description: 'Generate SEO metadata and keywords'
    }
  };
};